import { BOT_STATUS } from "./constants";

export const ASSISTANT_CHANNELS = ["web", "instagram", "whatsapp"];

const OPAQUE_BOT_ID = /^sp_[a-z0-9]{6,}$/;

const RESERVED_NAMES = new Set([
  "whatsapp",
  "instagram",
  "website",
  "web",
  "web sitesi",
  "web sitesi asistanı",
  "channels",
  "ecommerce",
  "sonic prospect",
]);

export function isOpaqueBotId(slug) {
  return OPAQUE_BOT_ID.test(String(slug || ""));
}

export function defaultAssistantName(user) {
  const company = String(
    user?.company || user?.companyName || user?.organization_name || user?.org_name || "",
  ).trim();
  if (company && !RESERVED_NAMES.has(company.toLowerCase())) {
    return `${company} Asistanı`;
  }
  return "Site asistanı";
}

export function nameNeedsRename(bot) {
  return RESERVED_NAMES.has(String(bot?.name || "").trim().toLowerCase());
}

function webChannel(bot) {
  return (bot?.channels || []).find((row) => row?.channel_type === "web_embed") || null;
}

/** Per-channel status: live | setup | paused | off */
export function getChannelStatus(bot, channel) {
  if (channel === "web") {
    const row = webChannel(bot);
    const hasEmbed = Boolean(row?.slug || bot?.slug);
    if (bot?.status === BOT_STATUS.CLOSED || (row && row.enabled === false && hasEmbed)) {
      return "paused";
    }
    const url = String(bot?.websiteUrl || "").trim();
    const seen = bot?.embedLastSeenAt || bot?.knowledgeStats?.embed_last_seen_at;
    if (!url || !seen) return "setup";
    return "live";
  }
  if (channel === "instagram") {
    if (bot?.instagram?.paused) return "paused";
    return bot?.instagram?.connected ? "live" : "off";
  }
  if (channel === "whatsapp") {
    if (bot?.whatsapp?.paused) return "paused";
    return bot?.whatsapp?.connected ? "live" : "off";
  }
  return "off";
}

/** Bot is live when at least one channel is live. */
export function getBotStatus(bot) {
  const live = ASSISTANT_CHANNELS.some((channel) => getChannelStatus(bot, channel) === "live");
  return live ? "live" : "setup";
}

export function channelConnection(bot, channel) {
  const status = getChannelStatus(bot, channel);
  if (channel === "web") {
    const url = String(bot?.websiteUrl || "").trim();
    const domain = url.replace(/^https?:\/\//i, "").split("/")[0];
    if (status === "live" || status === "paused") return { state: "connected", value: domain };
    if (url || bot?.slug) return { state: "pending", value: domain };
    return { state: "off", value: "" };
  }
  const live = status === "live" || status === "paused";
  if (channel === "instagram") {
    const handle = bot?.instagram?.username
      ? `@${String(bot.instagram.username).replace(/^@/, "")}`
      : bot?.instagram?.igUserId || "";
    return live ? { state: "connected", value: handle } : { state: "off", value: "" };
  }
  if (channel === "whatsapp") {
    const phone = bot?.whatsapp?.displayPhone || bot?.whatsapp?.phoneNumberId || "";
    return live ? { state: "connected", value: phone } : { state: "off", value: "" };
  }
  return { state: "off", value: "" };
}

export function connectedChannels(bot) {
  return ASSISTANT_CHANNELS.filter((channel) => getChannelStatus(bot, channel) !== "off");
}

/** Published once the assistant has been taken live, even if the embed is not detected yet. */
export function assistantPublished(bot) {
  return (
    bot?.status === BOT_STATUS.ACTIVE ||
    bot?.status === BOT_STATUS.CLOSED ||
    Boolean(bot?.embedLastSeenAt)
  );
}

export function statusTagColor(status) {
  if (status === "live") return "success";
  if (status === "paused" || status === "off") return "default";
  return "warning";
}

/** @deprecated use getBotStatus */
export function channelStatus(bot) {
  return getBotStatus(bot);
}

export function channelKind(bot) {
  if (bot?.instagram?.connected) return "instagram";
  if (bot?.whatsapp?.connected) return "whatsapp";
  return "web";
}

export function formatUsdTr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

export function formatUsdCentsTr(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  return formatUsdTr(n / 100);
}
